import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyTaxSettings.css";

const API_URL = "/api";

const emptyForm = {
  tax_name: "IVA",
  default_tax_rate: 0,
  apply_tax_to_sales: false,
  prices_include_tax: false,
  tax_responsibility: "",
  tax_regime: "",
  round_totals: true,
  rounding_precision: 0,
  is_active: true,
};

const toText = (value) => value ?? "";

const normalizeTaxSettings = (data = {}) => ({
  ...emptyForm,
  ...data,
  tax_name: toText(data.tax_name) || "IVA",
  default_tax_rate: data.default_tax_rate ?? 0,
  apply_tax_to_sales:
    data.apply_tax_to_sales ?? false,
  prices_include_tax:
    data.prices_include_tax ?? false,
  tax_responsibility:
    toText(data.tax_responsibility),
  tax_regime: toText(data.tax_regime),
  round_totals: data.round_totals ?? true,
  rounding_precision:
    data.rounding_precision ?? 0,
  is_active: data.is_active ?? true,
});

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

export default function CompanyTaxSettings({
  readOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/settings/tax/me`,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeTaxSettings(response.data));
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar la configuración de impuestos."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (readOnly) {
      return;
    }

    const taxRate = Number(form.default_tax_rate || 0);
    const precision = Number(form.rounding_precision || 0);

    if (!toText(form.tax_name).trim()) {
      toast.warning("El nombre del impuesto es obligatorio.");
      return;
    }

    if (taxRate < 0 || taxRate > 100) {
      toast.warning("La tarifa debe estar entre 0 y 100 %.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        tax_name: toText(form.tax_name)
          .trim()
          .toUpperCase(),
        default_tax_rate: taxRate,
        apply_tax_to_sales:
          form.apply_tax_to_sales,
        prices_include_tax:
          form.prices_include_tax,
        tax_responsibility:
          toText(form.tax_responsibility).trim() || null,
        tax_regime:
          toText(form.tax_regime).trim() || null,
        round_totals: form.round_totals,
        rounding_precision: precision,
        is_active: form.is_active,
      };

      const response = await axios.put(
        `${API_URL}/settings/tax/me`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeTaxSettings(response.data));

      toast.success(
        "Configuración de impuestos actualizada."
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible guardar la configuración de impuestos."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        Cargando configuración de impuestos...
      </div>
    );
  }

  return (
    <form
      className="tax-settings-form"
      onSubmit={handleSubmit}
    >
      <div className="tax-settings-heading">
        <div>
          <h3>Configuración de impuestos</h3>
          <p>
            Define el impuesto principal que se aplicará
            en ventas y facturas.
          </p>
        </div>

        <span
          className={
            form.is_active
              ? "tax-status active"
              : "tax-status inactive"
          }
        >
          {form.is_active
            ? "Impuestos activos"
            : "Impuestos inactivos"}
        </span>
      </div>

      <fieldset
        className="tax-settings-fieldset"
        disabled={readOnly}
      >
        <section className="tax-settings-section">
          <h4>Impuesto predeterminado</h4>

          <div className="tax-settings-grid">
            <div className="floating-field">
              <input
                id="tax_name"
                name="tax_name"
                value={form.tax_name}
                onChange={handleChange}
                placeholder=" "
                maxLength={50}
                required
              />
              <label htmlFor="tax_name">
                Nombre del impuesto
              </label>
            </div>

            <div className="floating-field">
              <input
                id="default_tax_rate"
                name="default_tax_rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.default_tax_rate}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="default_tax_rate">
                Tarifa predeterminada (%)
              </label>
            </div>
          </div>

          <div className="tax-switches">
            <label className="tax-switch">
              <input
                type="checkbox"
                name="apply_tax_to_sales"
                checked={form.apply_tax_to_sales}
                onChange={handleChange}
              />
              <span>
                Aplicar automáticamente el impuesto
                en las ventas
              </span>
            </label>

            <label className="tax-switch">
              <input
                type="checkbox"
                name="prices_include_tax"
                checked={form.prices_include_tax}
                onChange={handleChange}
              />
              <span>
                Los precios de venta ya incluyen el impuesto
              </span>
            </label>
          </div>
        </section>

        <section className="tax-settings-section">
          <h4>Información tributaria</h4>

          <div className="tax-settings-grid">
            <div className="floating-field">
              <input
                id="tax_responsibility"
                name="tax_responsibility"
                value={form.tax_responsibility}
                onChange={handleChange}
                placeholder=" "
                maxLength={150}
              />
              <label htmlFor="tax_responsibility">
                Responsabilidad tributaria
              </label>
            </div>

            <div className="floating-field">
              <input
                id="tax_regime"
                name="tax_regime"
                value={form.tax_regime}
                onChange={handleChange}
                placeholder=" "
                maxLength={100}
              />
              <label htmlFor="tax_regime">
                Régimen tributario
              </label>
            </div>
          </div>
        </section>

        <section className="tax-settings-section">
          <h4>Redondeo de valores</h4>

          <div className="tax-settings-grid">
            <div className="floating-field">
              <select
                id="rounding_precision"
                name="rounding_precision"
                value={form.rounding_precision}
                onChange={handleChange}
                disabled={
                  readOnly || !form.round_totals
                }
              >
                <option value="0">Sin decimales</option>
                <option value="1">1 decimal</option>
                <option value="2">2 decimales</option>
                <option value="3">3 decimales</option>
                <option value="4">4 decimales</option>
              </select>
              <label htmlFor="rounding_precision">
                Precisión de redondeo
              </label>
            </div>
          </div>

          <div className="tax-switches">
            <label className="tax-switch">
              <input
                type="checkbox"
                name="round_totals"
                checked={form.round_totals}
                onChange={handleChange}
              />
              <span>
                Redondear los totales de ventas y facturas
              </span>
            </label>
          </div>
        </section>

        <section className="tax-settings-section tax-danger-zone">
          <h4>Estado del módulo</h4>

          <label className="tax-switch">
            <input
              type="checkbox"
              name="is_active"
              checked={form.is_active}
              onChange={handleChange}
            />
            <span>
              Habilitar el módulo de impuestos
            </span>
          </label>
        </section>
      </fieldset>

      <div className="tax-settings-actions">
        {!readOnly ? (
          <button type="submit" disabled={saving}>
            {saving
              ? "Guardando..."
              : "Guardar configuración"}
          </button>
        ) : (
          <span className="settings-view-only">
            Solo consulta
          </span>
        )}
      </div>
    </form>
  );
}